import { type FocusEvent, useCallback, useEffect } from 'react';
import type { Key } from 'react-aria-components';
import { CheckoutSection } from '@/common.styled';
import { AddressFields } from '@/components/AddressFields';
import { Heading } from '@/components/Heading';
import { StoredDeliveryAddresses } from '@/components/StoredDeliveryAddresses';
import {
  FieldName,
  ValidationMessage,
  ValidationSectionKey,
} from '@/constants';
import { usePageData } from '@/contexts/pageContext';
import { useShipping } from '@/contexts/shippingContext';
import type { RequiredFormField } from '@/contexts/types';
import { useUser } from '@/contexts/userContext';
import { useValidation } from '@/contexts/validationContext';
import { useGetCounties } from '@/hooks/useGetCounties';
import type { AddressFieldKey } from '@/types';
import { doNothingOnSubmit } from '@/utils/doNothingOnSubmit';
import { getCountyLabel } from '@/utils/getCountyLabel';
import { getPostalCodeLabel } from '@/utils/getPostalCodeLabel';
import { getRequiredAddressFields } from '@/utils/getRequiredAddressFields';
import { validatePhone } from '@/utils/validatePhone';

const headingId = 'deliveryAddress';

export const DeliveryAddress = () => {
  const { countries } = usePageData();
  const {
    orderPayload: { shippingAddress },
    setDeliveryAddress,
  } = useShipping();
  const { deliveryAddresses, isAuthenticated } = useUser();
  const showStoredAddresses = isAuthenticated && deliveryAddresses.length > 0;

  const { countryCode } = shippingAddress;
  const counties = useGetCounties(countryCode);
  const countyLabel = getCountyLabel(countryCode);
  const postalCodeLabel = getPostalCodeLabel(countryCode);

  const {
    addressFieldErrors,
    clearAddressFieldError,
    clearValidationError,
    errorMessages,
    setAddressFieldError,
  } = useValidation();

  const addressError = errorMessages[ValidationSectionKey.ShippingAddress];

  const validationErrors = addressFieldErrors.delivery;

  useEffect(() => {
    if (Object.values(validationErrors).filter(Boolean).length === 0) {
      clearValidationError(ValidationSectionKey.ShippingAddress);
    }
  }, [clearValidationError, validationErrors]);

  const updateValidation = useCallback(
    (fieldName: RequiredFormField, value: string) => {
      // Phone validation: check format and length
      if (fieldName === FieldName.Phone) {
        if (!validatePhone(value)) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.InvalidPhone,
          );
        } else if (value.length > 19) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.PhoneTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      } else if (fieldName === FieldName.FirstName) {
        if (value.length > 20) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.FirstNameTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      } else if (fieldName === FieldName.LastName) {
        if (value.length > 20) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.LastNameTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      } else if (fieldName === FieldName.AddressLine1) {
        if (value.length > 50) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.AddressLineTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      } else if (fieldName === FieldName.City) {
        if (value.length > 50) {
          setAddressFieldError(
            'delivery',
            fieldName,
            ValidationMessage.CityTooLong,
          );
        } else {
          clearAddressFieldError('delivery', fieldName);
        }
      }
      // For other fields (postalCode, countryCode, etc.), don't clear errors here
      // They have their own validation logic
    },
    [clearAddressFieldError, setAddressFieldError],
  );

  const handlePhoneBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (value) {
        // Validate phone: check format and total length (<= 19 chars)
        updateValidation(FieldName.Phone, value);
      } else {
        setAddressFieldError(
          'delivery',
          FieldName.Phone,
          ValidationMessage.MissingField,
        );
      }
    },
    [setAddressFieldError, updateValidation],
  );

  const handleChange = useCallback(
    (fieldName: AddressFieldKey) => (value: Key | null) => {
      const rawValue = typeof value === 'string' ? value : '';
      const normalisedValue = rawValue.trim();

      // Validate raw length (including spaces) before trimming for length-constrained fields
      if (
        fieldName === FieldName.FirstName ||
        fieldName === FieldName.LastName ||
        fieldName === FieldName.Phone ||
        fieldName === FieldName.AddressLine1 ||
        fieldName === FieldName.City
      ) {
        let errorMessage: string | null = null;
        if (fieldName === FieldName.FirstName && rawValue.length > 20) {
          errorMessage = ValidationMessage.FirstNameTooLong;
        } else if (fieldName === FieldName.LastName && rawValue.length > 20) {
          errorMessage = ValidationMessage.LastNameTooLong;
        } else if (fieldName === FieldName.Phone && rawValue.length > 19) {
          errorMessage = ValidationMessage.PhoneTooLong;
        } else if (
          fieldName === FieldName.AddressLine1 &&
          rawValue.length > 50
        ) {
          errorMessage = ValidationMessage.AddressLineTooLong;
        } else if (fieldName === FieldName.City && rawValue.length > 50) {
          errorMessage = ValidationMessage.CityTooLong;
        }

        if (errorMessage) {
          setAddressFieldError(
            'delivery',
            fieldName as RequiredFormField,
            errorMessage,
          );
        }
      }

      const requiredFields = getRequiredAddressFields(countryCode);

      const isRequired = requiredFields.some(key => key === fieldName);

      // Validation only for required fields
      if (isRequired) {
        if (normalisedValue) {
          // For length-constrained fields, use raw value for length validation
          // For phone, always use raw value to check total length including formatting
          const isLengthConstrained =
            fieldName === FieldName.FirstName ||
            fieldName === FieldName.LastName ||
            fieldName === FieldName.Phone ||
            fieldName === FieldName.AddressLine1 ||
            fieldName === FieldName.City;
          const valueToValidate =
            fieldName === FieldName.Phone || isLengthConstrained
              ? rawValue
              : normalisedValue;
          updateValidation(fieldName as RequiredFormField, valueToValidate);
        } else {
          setAddressFieldError(
            'delivery',
            fieldName as RequiredFormField,
            ValidationMessage.MissingField,
          );
        }
      }
      // Note: Error clearing for length-constrained fields is handled by updateValidation
      // No need to clear errors here to avoid conflicts

      // Address update for ALL fields (required and optional)
      // Store trimmed value for text fields to prevent storing leading/trailing spaces
      if (fieldName === FieldName.Country) {
        const selectedCountry = countries?.find(
          country => value === country.countryCode,
        );
        if (selectedCountry) {
          // When country changes, also clear any previously selected county
          // so the user must select a valid county for the new country
          setDeliveryAddress({
            [FieldName.Country]: selectedCountry.countryCode,
            [FieldName.County]: '',
          });
        }
      } else if (fieldName === FieldName.County) {
        const selectedCounty = counties?.find(
          county => value === county.countyCode,
        );
        if (selectedCounty) {
          setDeliveryAddress({
            [FieldName.County]: selectedCounty.countyCode,
          });
        }
      } else {
        // Store trimmed value for text fields to avoid storing leading/trailing spaces
        const valueToStore =
          typeof value === 'string' ? normalisedValue : value;
        setDeliveryAddress({ [fieldName]: valueToStore });
      }
    },
    [
      counties,
      countries,
      countryCode,
      clearAddressFieldError,
      setAddressFieldError,
      setDeliveryAddress,
      updateValidation,
    ],
  );

  return (
    <CheckoutSection data-validation-key={ValidationSectionKey.ShippingAddress}>
      <Heading id={headingId} level={2}>
        Delivery address
      </Heading>
      <div className={addressError ? 'has-validation-error' : undefined}>
        {addressError ? (
          <p aria-live="assertive" className="validation-error">
            {addressError}
          </p>
        ) : null}
        {showStoredAddresses ? (
          <StoredDeliveryAddresses headingId={headingId} />
        ) : (
          <AddressFields
            address={shippingAddress}
            countyLabel={countyLabel}
            handleChange={handleChange}
            handlePhoneBlur={handlePhoneBlur}
            handleSubmit={doNothingOnSubmit}
            postalCodeLabel={postalCodeLabel}
            validationErrors={validationErrors}
          />
        )}
      </div>
    </CheckoutSection>
  );
};
